import React, { useEffect, useMemo, useState, useRef, ReactNode } from "react";
import { Container, Table, Form, Button, Spinner, Alert } from "react-bootstrap";
import { toast } from "react-toastify";

import Template from "../../template";
import api from "../../services/api";
import * as S from "./styled";

type TriStateProps = {
  checked: boolean;
  indeterminate: boolean;
  onToggle: () => void;
  label?: ReactNode;
  title?: string;
  disabled?: boolean;
  ariaLabel?: string;
};

function TriStateCheckbox({
  checked,
  indeterminate,
  onToggle,
  label,
  title,
  disabled,
  ariaLabel,
}: TriStateProps) {
  const ref = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const ariaChecked: React.AriaAttributes["aria-checked"] =
    indeterminate ? "mixed" : checked;

  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "flex-start",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none",
      }}
      title={title}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={indeterminate ? false : checked}
        disabled={disabled}
        onChange={() => onToggle()}
        aria-label={
          ariaLabel ?? (typeof label === "string" ? label : undefined)
        }
        aria-checked={ariaChecked}
        style={{ width: 16, height: 16, cursor: disabled ? "not-allowed" : "pointer" }}
      />
      {label ? <span>{label}</span> : null}
    </label>
  );
}

// seus planos
export const Plan = {
  ESSENCIAL: "ESSENCIAL",
  PLUS: "PLUS",
  VIP: "VIP",
  PREMIUM: "PREMIUM",
  PRESENCIAL: "PRESENCIAL",
} as const;

type PlanType = typeof Plan[keyof typeof Plan];

type TemaIA = {
  id: number;
  nome: string;
  genero?: string;
  aula?: string;
  descricao?: string;
  arquivo?: string;
};

type VinculoPlanoTema = {
  plano_nome: PlanType;
  tema_id: number;
  tema_nome: string;
};

export default function PlanosTemasAdmin() {

  const selecaoInicialRef = useRef<Record<number, Set<PlanType>>>({});

  const cloneSelecao = (src: Record<number, Set<PlanType>>) => {
    const out: Record<number, Set<PlanType>> = {};
    for (const [k, v] of Object.entries(src)) {
      out[Number(k)] = new Set(Array.from(v ?? []));
    }
    return out;
  };

  const setsEqual = (a?: Set<PlanType>, b?: Set<PlanType>) => {
    const A = a ?? new Set<PlanType>();
    const B = b ?? new Set<PlanType>();
    if (A.size !== B.size) return false;

    const arr = Array.from(A);
    for (let i = 0; i < arr.length; i++) {
      const x = arr[i];
      if (!B.has(x)) return false;
    }
    return true;
  };


  const getChangedTemaIds = () => {
    const changed: number[] = [];
    for (const t of temas) {
      const cur = mapSelecao[t.id] ?? new Set<PlanType>();
      const ini = selecaoInicialRef.current[t.id] ?? new Set<PlanType>();
      if (!setsEqual(cur, ini)) changed.push(t.id);
    }
    return changed;
  };

  const planos = useMemo(() => Object.values(Plan) as PlanType[], []);

  const planosSempreAtivos = useMemo(() => [Plan.PRESENCIAL] as PlanType[], []);
  const planosVisiveis = useMemo(
    () => [Plan.ESSENCIAL, Plan.PLUS, Plan.VIP, Plan.PREMIUM] as PlanType[],
    []
  );

  const [loading, setLoading] = useState(true);
  const [temas, setTemas] = useState<TemaIA[]>([]);
  const [savingAll, setSavingAll] = useState(false);
  const [vinculos, setVinculos] = useState<VinculoPlanoTema[]>([]);
  const [mapSelecao, setMapSelecao] = useState<Record<number, Set<PlanType>>>(
    {}
  );
  const [savingTemaId, setSavingTemaId] = useState<number | null>(null);

  const togglePlanHeader = (plano: PlanType) => {
    const st = getHeaderStateFast(plano);
    if (st.all) limparPlano(plano);
    else marcarTudoNoPlano(plano);
  };

  const planCounts = useMemo<Record<PlanType, number>>(() => {
    const counts = Object.fromEntries(
      planosVisiveis.map((p) => [p, 0])
    ) as Record<PlanType, number>;

    for (const t of temas) {
      const set = mapSelecao[t.id];
      for (const p of planosVisiveis) {
        if (set?.has(p)) counts[p]++;
      }
    }

    return counts;
  }, [temas, mapSelecao, planosVisiveis]);


const getHeaderStateFast = (plano: PlanType) => {
  const total = temas.length;
  const checkedCount = planCounts[plano] ?? 0;
  const all = total > 0 && checkedCount === total;
  const none = checkedCount === 0;
  const indeterminate = !all && !none;
  return { all, none, indeterminate, checkedCount, total };
};


  const buildSelecaoInicial = (temasList: TemaIA[], vinculosList: VinculoPlanoTema[]) => {
    const byTema: Record<number, Set<PlanType>> = {};

    // começa com presencial sempre ativo
    for (let i = 0; i < temasList.length; i++) {
      const t = temasList[i];
      byTema[t.id] = new Set<PlanType>(planosSempreAtivos);
    }

    // aplica vínculos do backend por cima
    for (let i = 0; i < vinculosList.length; i++) {
      const v = vinculosList[i];
      if (!byTema[v.tema_id]) byTema[v.tema_id] = new Set<PlanType>(planosSempreAtivos);
      byTema[v.tema_id].add(v.plano_nome);
    }

    // segurança extra: garante presencial em todos
    for (let i = 0; i < temasList.length; i++) {
      const t = temasList[i];
      byTema[t.id].add(Plan.PRESENCIAL);
    }

    return byTema;
  };


  const togglePlano = (temaId: number, plano: PlanType) => {
    if (planosSempreAtivos.includes(plano)) return; // presencial sempre ativo, não pode desmarcar

    setMapSelecao((prev) => {
      const next = { ...prev };
      const setAtual = new Set(next[temaId] ?? []);
      if (setAtual.has(plano)) setAtual.delete(plano);
      else setAtual.add(plano);
      next[temaId] = setAtual;
      return next;
    });
  };

  const selectAll = (temaId: number) => {
    setMapSelecao((prev) => ({
      ...prev,
      [temaId]: new Set(planos),
    }));
  };

  const clearAll = (temaId: number) => {
    setMapSelecao((prev) => ({
      ...prev,
      [temaId]: new Set(planosSempreAtivos),
    }));
  };


  const salvarTema = async (temaId: number) => {
    try {
      setSavingTemaId(temaId);

      const planosSelecionados = Array.from(mapSelecao[temaId] ?? []);
      const planosParaSalvar = Array.from(new Set([...planosSelecionados, ...planosSempreAtivos]));

      await api.post("/temas/vincular", {
        tema_id: temaId,
        planos: planosParaSalvar,
      });

      selecaoInicialRef.current[temaId] = new Set(planosParaSalvar);

      toast.success("Vínculos atualizados com sucesso!");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao salvar vínculos do tema.");
    } finally {
      setSavingTemaId(null);
    }
  };

  const temaMudou = (temaId: number) => {
  const cur = mapSelecao[temaId] ?? new Set<PlanType>();
  const ini = selecaoInicialRef.current[temaId] ?? new Set<PlanType>();
  return !setsEqual(cur, ini);
};


    const selectAllGlobal = () => {
    setMapSelecao((prev) => {
      const next: Record<number, Set<PlanType>> = { ...prev };
      for (const t of temas) next[t.id] = new Set(planos);
      return next;
    });
  };

  const clearAllGlobal = () => {
    setMapSelecao((prev) => {
      const next: Record<number, Set<PlanType>> = { ...prev };
      for (const t of temas) next[t.id] = new Set(planosSempreAtivos);
      return next;
    });
  };

  const saveAllGlobal = async () => {
    try {
      setSavingAll(true);

      const changedIds = getChangedTemaIds();

      if (changedIds.length === 0) {
        toast.info("Nada mudou (nenhum tema para salvar).");
        return;
      }

      for (const temaId of changedIds) {
        const planosSelecionados = Array.from(mapSelecao[temaId] ?? []);
        const planosParaSalvar = Array.from(
          new Set([...planosSelecionados, ...planosSempreAtivos])
        );

        await api.post("/temas/vincular", {
          tema_id: temaId,
          planos: planosParaSalvar,
        });

        selecaoInicialRef.current[temaId] = new Set(planosParaSalvar);
      }

      toast.success(`Salvo! Atualizados ${changedIds.length} tema(s).`);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao salvar todos os vínculos.");
    } finally {
      setSavingAll(false);
    }
  };

  const marcarTudoNoPlano = (plano: PlanType) => {
    setMapSelecao((prev) => {
      const next: Record<number, Set<PlanType>> = { ...prev };
      for (const t of temas) {
        const s = new Set(next[t.id] ?? []);
        s.add(plano);
        next[t.id] = s;
      }
      return next;
    });
  };

  const limparPlano = (plano: PlanType) => {
    setMapSelecao((prev) => {
      const next: Record<number, Set<PlanType>> = { ...prev };
      for (const t of temas) {
        const s = new Set(next[t.id] ?? []);
        s.delete(plano);
        next[t.id] = s;
      }
      return next;
    });
  };


  const recarregar = async () => {
    setLoading(true);
    try {
      // 1) todos os temas IA
      const respTemas = await api.get("/pegarTemasGeral");
      // 2) todos os vínculos plano↔tema (admin)
      const respVinculos = await api.get("/planos-temas");

      setTemas(respTemas.data ?? []);
      setVinculos(respVinculos.data ?? []);

      const initial = buildSelecaoInicial(respTemas.data ?? [], respVinculos.data ?? []);
      selecaoInicialRef.current = cloneSelecao(initial);
      setMapSelecao(cloneSelecao(initial));
    } catch (e) {
      console.error(e);
      toast.error("Erro ao carregar temas/vínculos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    recarregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Template>
      <S.MainWrapper>
        <Container fluid>
          <S.MainContent>
            <S.HeaderCard>
              <S.HeaderLeft>
                <h3>Planos - Temas (IA)</h3>
                <p>Marque quais temas ficam disponíveis em cada plano.</p>
              </S.HeaderLeft>

              <S.HeaderActions>
                <Button
                  variant="outline-secondary"
                  onClick={selectAllGlobal}
                  disabled={loading || temas.length === 0}
                >
                  Marcar tudo
                </Button>

                <Button
                  variant="outline-secondary"
                  onClick={clearAllGlobal}
                  disabled={loading || temas.length === 0}
                >
                  Limpar tudo
                </Button>

                <Button
                  variant="primary"
                  onClick={saveAllGlobal}
                  disabled={loading || temas.length === 0 || savingAll}
                >
                  {savingAll ? (
                    <>
                      <Spinner animation="border" size="sm" /> Salvando…
                    </>
                  ) : (
                    "Salvar tudo"
                  )}
                </Button>

                <Button variant="outline-primary" onClick={recarregar} disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : "Recarregar"}
                </Button>
              </S.HeaderActions>
            </S.HeaderCard>
          </S.MainContent>
          {loading && (
            <S.MainContent>
              <div className="text-center p-4">
                <Spinner animation="border" />
                <div className="mt-2 text-muted">Carregando…</div>
              </div>
            </S.MainContent>
          )}

          {!loading && temas.length === 0 && (
            <S.MainContent>
              <Alert variant="warning" className="mb-0">
                Nenhum tema encontrado em <strong>/pegarTemasGeral</strong>.
              </Alert>
            </S.MainContent>
          )}

            {!loading && temas.length > 0 && (
              <S.MainContent>
                <S.TableWrap>
                  <Table hover bordered className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 380 }}>Tema</th>
                          {planosVisiveis.map((p) => (
                            <th key={p} className="text-center" style={{ minWidth: 120 }}>
                              <div className="d-flex flex-column align-items-center" style={{ gap: 4 }}>
                                {(() => {
                                  const st = getHeaderStateFast(p);
                                  return (
                                    <TriStateCheckbox
                                      label={
                                        <span style={{ 
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          lineHeight: 1.1,
                                          minWidth: 72
                                          }}>
                                          <span style={{fontSize: 14, fontWeight: 700 }}>{p}</span>
                                          <span style={{ fontSize: 11, fontWeight: 500, color: "#6b7280" }}>
                                            ({st.checkedCount}/{st.total})
                                          </span>
                                        </span>
                                      }
                                      checked={st.all}
                                      indeterminate={st.indeterminate}
                                      disabled={loading || temas.length === 0}
                                      title={
                                        st.all
                                          ? `Clique para limpar todos do ${p}`
                                          : st.indeterminate
                                          ? `Alguns marcados — clique para marcar todos do ${p}`
                                          : `Clique para marcar todos do ${p}`
                                      }
                                      onToggle={() => togglePlanHeader(p)}
                                    />
                                  );
                                })()}

                              </div>
                            </th>
                          ))}
                        <th className="text-center" style={{ minWidth: 260 }}>
                          Ações
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {temas.map((t) => (
                        <tr key={t.id}>
                          <td>
                            <S.TemaTitle>{t.nome}</S.TemaTitle>

                          <S.MetaRow>
                              <S.Chip>ID: {t.id}</S.Chip>
                              {t.genero ? <S.Chip>Gênero: {t.genero}</S.Chip> : null}
                              {t.aula ? <S.Chip>Aula: {t.aula}</S.Chip> : null}
                            </S.MetaRow>
                          </td>

                          {planosVisiveis.map((p) => {
                            const checked = (mapSelecao[t.id] ?? new Set()).has(p);
                            return (
                              <S.CenterCell key={p}>
                                <Form.Check
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => togglePlano(t.id, p)}
                                />
                              </S.CenterCell>
                            );
                          })}

                          <S.ActionsCell>
                            <div className="d-flex justify-content-center" style={{ gap: 8 }}>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => selectAll(t.id)}
                              >
                                Marcar tudo
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => clearAll(t.id)}
                              >
                                Limpar
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => salvarTema(t.id)}
                                disabled={savingTemaId === t.id || !temaMudou(t.id)}
                              >
                                {savingTemaId === t.id ? (
                                  <>
                                    <Spinner animation="border" size="sm" /> Salvando…
                                  </>
                                ) : (
                                  "Salvar"
                                )}
                              </Button>
                            </div>
                          </S.ActionsCell>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </S.TableWrap>
              </S.MainContent>
            )}

        </Container>
      </S.MainWrapper>
    </Template>
  );
}